// Copy for components/TrialSignupDialog.vue, one entry per locale. The dialog is never
// prerendered (it mounts closed and opens on a "Start free" click), so unlike page copy it
// can follow the URL's locale directly: there is no English server HTML to hydrate against
// and nothing for the generated editions' dictionaries to translate. English stays in the
// component bundle; the other locales load as one lazy chunk (see loadTrialDialogCopy).
//
// Placeholders: {price} the monthly price after the trial, {plan} plan name, {seats} included seats, {percent} and {months} from
// data/founding-offer.ts. "Your privacy choices" and "Privacy Policy" reuse the cookie
// banner's wording from data/home-i18n/<code>.json.

export interface TrialDialogCopy {
  close: string;
  title: string;
  summary: string;
  detailsLabel: string;
  cancelTitle: string;
  cancelNote: string;
  trialTerms: string;
  trialTermsPlan: string;
  timeTitle: string;
  timeNote: string;
  formTitle: string;
  formNext: string;
  plan: string;
  planSeats: string;
  foundingOffer: string;
  emailLabel: string;
  submit: string;
  submitting: string;
  errorInvalid: string;
  errorPlus: string;
  errorSave: string;
  consentBefore: string;
  privacyChoices: string;
  consentAfter: string;
  privacyPolicy: string;
}

export const englishTrialDialogCopy: TrialDialogCopy = {
  close: 'Close trial signup',
  title: 'Try Workspace369 free for 14 days.',
  summary:
    'Proposals, invoices, projects, clients and payments in one workspace. Start with your email, then finish setting up in Workspace369.',
  detailsLabel: 'Trial details',
  cancelTitle: 'Cancel anytime',
  cancelNote: 'Cancel before day 14 and pay nothing.',
  trialTerms:
    '14-day free trial. Card required. After the trial: from {price}/month. Renews automatically every month until you cancel. Cancel anytime in Settings → Billing.',
  trialTermsPlan:
    '14-day free trial. Card required. After the trial: {price}/month for {plan}. Renews automatically every month until you cancel. Cancel anytime in Settings → Billing.',
  timeTitle: 'About two minutes',
  timeNote: 'Email, password and your business name.',
  formTitle: 'Start with your email',
  formNext: 'Next, you set a password in Workspace369.',
  plan: '{plan} plan',
  planSeats: '{plan} plan · {seats} seats included',
  foundingOffer: 'Founding offer on yearly plans: your first year for the price of {months} months',
  emailLabel: 'Email',
  submit: 'Continue',
  submitting: 'One moment...',
  errorInvalid: 'Enter a valid email address, like you@yourbusiness.com.',
  errorPlus: 'Use your primary email without a plus alias.',
  errorSave: 'We could not save your place. Please try again.',
  consentBefore:
    'By continuing, you agree that we may save your email and contact you to help complete account setup or provide account and security updates. You can opt out of non-essential setup emails at any time. This does not enroll you in marketing emails. Unless you have opted out in ',
  privacyChoices: 'Your privacy choices',
  consentAfter: ', we also measure which ads led to your sign-up, including with Meta.',
  privacyPolicy: 'Privacy policy',
};

export const loadTrialDialogCopy = async (localeCode: string): Promise<TrialDialogCopy> => {
  if (localeCode === 'en') return englishTrialDialogCopy;
  const { trialDialogCopy } = await import('./trial-dialog-copy-locales');
  return trialDialogCopy[localeCode] || englishTrialDialogCopy;
};

export const formatTrialDialogCopy = (template: string, values: Record<string, string | number>) =>
  template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
